import { classes } from "common/theme/theme";
import { CustomerSelector } from "portal/components/customers/CustomerSelector";
import { doNothing } from "portal/utils/functions";
import { LOCALSTORAGE_CUSTOMER_SCOPE } from "portal/utils/localStorage";
import { useCustomerScopeFilter } from "portal/components/customers/useCustomerScopeFilter";
import { useTranslation } from "react-i18next";
import { WhenTrue } from "common/utils/types";
import React, { FC } from "react";

type CustomerScopeSelectorProps = {
  localStorageKey?: string;
  className?: string;
} & WhenTrue<
  "readOnly",
  {
    value: string | undefined; // for display; when editable, value is stored in localstorage
  }
>;

/**
 * Customer scoping for viewing pages as a certain customer.
 * Visible only to users who can see other customers; others are scoped to self.
 */
export const CustomerScopeSelector: FC<CustomerScopeSelectorProps> = ({
  localStorageKey = LOCALSTORAGE_CUSTOMER_SCOPE,
  className,
  readOnly,
  value: readOnlyValueOverride,
}) => {
  const { t } = useTranslation();
  const {
    canSeeOtherCustomers,
    customerUuid: selectedCustomerUuid,
    setCustomerUuid,
  } = useCustomerScopeFilter(localStorageKey);

  if (!canSeeOtherCustomers) {
    return;
  }

  return (
    <CustomerSelector
      valueKey="uuid"
      value={readOnly ? readOnlyValueOverride : selectedCustomerUuid}
      onChange={readOnly ? doNothing : setCustomerUuid}
      readOnly={readOnly}
      deferClearUntilBlur
      emptyText={t("components.customers.CustomerSelector.viewCustomer")}
      small
      className={classes("!mt-0 min-w-0 w-full max-w-80 shrink", className)}
    />
  );
};
